import { IconAlertTriangle, IconHistory } from "@tabler/icons-react";

import { ActionIcon } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { safeNumber } from "common/utils";
import { ProductCodeSelection } from "pages/admin/components/ProductCodeSelection";
import {
  ProductQuickSearchInput,
  ProductQuickSearchInputHandle,
} from "pages/admin/components/ProductQuickSearchInput";
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useProductStore } from "stores/useProductStore";
import { OrderEntryRowProps } from "types/interface.types";
import { OrderEntryGridRow } from "./DataEntryHeaderDefinitions";

/**
 * Note: This component uses intrinsic controls as oppose to mantine <NumberInput> and <Select> becuase of how expensive
 * it is with rendering.
 * especially when we have large sets of entries to input...
 *
 */
export interface OrderEntryRowHandle {
  focus: () => void;
}
/**
 * Optimized row component for Virtuoso.
 * Uses local state for inputs and commits changes onBlur.
 */
const EntryRowComponent = forwardRef<OrderEntryRowHandle, OrderEntryRowProps>(
  (
    {
      entry,
      index,
      isLastRow,
      orderStage,
      debtorPriceLevel,
      onUpdate,
      onRemove,
      onNewEntry,
    },
    ref,
  ) => {
    const [localProductId, setLocalProductId] = useState(entry.productId);
    const [localProductCode, setLocalProductCode] = useState(entry.productCode);
    const [localOrdered, setLocalOrdered] = useState(entry.ordered);
    const [localDiscount, setLocalDiscount] = useState<string>(
      entry.discount === null ? "" : String(entry.discount),
    );
    const [localUnitPrice, setLocalUnitPrice] = useState<string>(
      entry.unitPrice === null ? "" : String(entry.unitPrice),
    );
    const [localSupplied, setLocalSupplied] = useState(entry.supplied);
    const [localRef, setLocalRef] = useState(entry.ref);
    const [isNewRow, setIsNewRow] = useState(entry.id === 0);

    const allProducts = useProductStore((s) => s.allProducts);

    // Ref to the product input (inside ProductQuickSearchInput)
    //const productInputRef = useRef<{ focus: () => void } | null>(null);
    const productInputRef = useRef<ProductQuickSearchInputHandle | null>(null);
    const deletedStyle = entry.softDelete
      ? {
          textDecoration: "line-through",
          opacity: 0.5,
          color: "var(--mantine-color-red-6)",
        }
      : {};

    // Expose focus() to parent
    useImperativeHandle(ref, () => ({
      focus() {
        productInputRef.current?.focus();
      },
    }));

    const handlePriceCalc = (
      productId?: number,
      productCode?: string,
      forceReCalc = false,
    ) => {
      if (
        forceReCalc ||
        localUnitPrice === null ||
        Number.isNaN(localUnitPrice) ||
        localUnitPrice === ""
      ) {
        const productIdToUse = productId ?? localProductId;
        const productCodeToUse = productCode ?? localProductCode;

        // Lookup product only if productId has changed
        const product = allProducts.items.find(
          (p) => Number(p.productCode) === productIdToUse,
        );

        if (product) {
          // sell price depends on debtor level
          let eachPrice = product.sellPrice1;
          const customerPriceLevel = debtorPriceLevel ?? 1;
          switch (customerPriceLevel) {
            case 2: {
              eachPrice = product.sellPrice2 ?? 0;
              break;
            }
            case 3: {
              eachPrice = product.sellPrice3 ?? 0;
              break;
            }
            case 4: {
              eachPrice = product.sellPrice4 ?? 0;
              break;
            }
            default: {
              eachPrice = product.sellPrice1 ?? 0;
              break;
            }
          }

          // this is the actual unit price depending on product code being E (unit) or C (carton)
          const unitPrice = safeNumber(
            eachPrice *
              (productCodeToUse === "E"
                ? (product.units ?? 1)
                : (product.unitsCtn ?? 1)),
          );

          // blank - recalc, 0 => valid and $0 item
          setLocalUnitPrice(String(unitPrice));
        }
      }
    };

    return (
      <OrderEntryGridRow data-row-index={index} orderStage={orderStage}>
        {/* Note: we use local variables as we need to implement onChange.  We don't want to bind directly to the parent as it will be very expensive with re-rendering */}
        {/* PRODUCT SEARCH INPUT */}
        <ProductQuickSearchInput
          ref={productInputRef}
          value={localProductId}
          matchOnSupplier={false}
          onChange={(newId) => {
            setLocalProductId(newId);
            if (newId !== entry.productId) {
              handlePriceCalc(newId);
              onUpdate({ ...entry, productId: newId }, true);

              setTimeout(() => {
                requestAnimationFrame(() => {
                  productInputRef.current?.blur();
                });
              }, 0);
            }
          }}
        />
        <ProductCodeSelection
          onChange={(code: string) => {
            setLocalProductCode(code);

            if (code !== entry.productCode) {
              handlePriceCalc(undefined, code, true);
              onUpdate(
                {
                  ...entry,
                  productCode: code,
                  unitPrice:
                    localUnitPrice.trim() === ""
                      ? null
                      : Number(localUnitPrice),
                },
                true,
              );
            }
          }}
          value={localProductCode}
        />
        <div style={{ position: "relative", width: "100%" }}>
          <input
            type="text"
            readOnly
            value={entry.description}
            className="mantine-input"
            style={{
              width: "100%",
              height: 32,
              minWidth: 0,
              paddingRight: 28, // make room for icon
              ...deletedStyle,
            }}
          />

          {(entry.onHand ?? 0) === 0 ? (
            <IconAlertTriangle
              size={16}
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                color: "red",
                pointerEvents: "none",
                ...deletedStyle,
              }}
            />
          ) : entry.isBackOrderItem ? (
            <IconHistory
              size={16}
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#000",
                pointerEvents: "none",
                ...deletedStyle,
              }}
            />
          ) : null}
        </div>
        <input
          type="number"
          value={localOrdered}
          readOnly={entry.softDelete ?? false}
          onChange={(e) => {
            const val = Number(e.target.value);
            if (!isNaN(val)) setLocalOrdered(val); // local only
          }}
          onBlur={(e) => {
            if (localOrdered !== entry.ordered) {
              onUpdate({ ...entry, ordered: localOrdered }); // commit to parent
            }
          }}
          className="mantine-input"
          style={{ width: "100%", height: 32, minWidth: 0, ...deletedStyle }}
        />
        <input
          type="number"
          value={localSupplied}
          readOnly={entry.softDelete ?? false}
          onChange={(e) => {
            const val = Number(e.target.value);
            if (!isNaN(val)) setLocalSupplied(val); // local only
          }}
          onBlur={(e) => {
            handlePriceCalc();
            if (localSupplied !== entry.supplied) {
              onUpdate({
                ...entry,
                supplied: localSupplied,
                ordered: localOrdered,
                unitPrice:
                  localUnitPrice.trim() === "" ? null : Number(localUnitPrice),
                discount:
                  localDiscount.trim() === "" ? null : Number(localDiscount),
              }); // commit to parent
            }
          }}
          className="mantine-input"
          style={{
            width: "100%",
            height: 32,
            minWidth: 0,
            backgroundColor: `${localSupplied > localOrdered ? "var(--mantine-color-yellow-1)" : "transparent"}`,
            ...deletedStyle,
          }}
        />
        {/* 0 = picking, 1 = packing, 2 = shipped */}
        {(orderStage ?? 0) !== 0 && (
          <>
            <input
              type="text"
              value={localUnitPrice}
              readOnly={entry.softDelete ?? false}
              onChange={(e) => {
                const val = e.target.value;

                // Allow blank
                if (val === "") {
                  setLocalUnitPrice("");
                  return;
                }

                // Allow only digits + optional decimal
                if (/^\d*\.?\d*$/.test(val)) {
                  setLocalUnitPrice(val);
                }
              }}
              onBlur={(e) => {
                if (localUnitPrice.trim() === "") {
                  handlePriceCalc(localProductId, localProductCode);
                  onUpdate(
                    {
                      ...entry,
                      unitPrice: null, // triggers recalc
                    },
                    true,
                  );
                  return;
                }
                // Convert to number
                const num = Number(localUnitPrice);

                if (num !== entry.unitPrice) {
                  onUpdate(
                    {
                      ...entry,
                      unitPrice: num,
                    },
                    false,
                  ); // commit to parent
                }
              }}
              className="mantine-input"
              style={{
                width: "100%",
                height: 32,
                minWidth: 0,
                ...deletedStyle,
              }}
            />
            <input
              type="number"
              value={localDiscount}
              readOnly={entry.softDelete ?? false}
              onChange={(e) => {
                {
                  const val = e.target.value;

                  // allow empty string (so user can clear input)
                  if (val.trim() === "") {
                    setLocalDiscount(val);
                    return;
                  }

                  // allow only valid numbers
                  if (Number.isFinite(Number(val))) {
                    setLocalDiscount(val);
                  }
                }
              }}
              onBlur={(e) => {
                let val = Number(localDiscount ?? 0);
                if (val > 100) val = 100;
                if (val >= 1) val = val / 100; // convert to decimal
                val = Number(val.toFixed(3));
                setLocalDiscount(String(val));
                if (val !== entry.discount) {
                  onUpdate({ ...entry, discount: val }); // commit to parent
                }
              }}
              className="mantine-input"
              style={{
                width: "100%",
                height: 32,
                minWidth: 0,
                ...deletedStyle,
              }}
            />
          </>
        )}
        <input
          type="text"
          value={localRef}
          readOnly={entry.softDelete ?? false}
          onChange={(e) => {
            setLocalRef(e.target.value); // local only
          }}
          onBlur={(e) => {
            if (localRef !== entry.ref) {
              onUpdate({ ...entry, ref: localRef }); // commit to parent
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Tab" && !e.shiftKey) {
              // only trigger new row if this is the last row
              if (isLastRow) {
                e.preventDefault(); // stop default tabbing
                onNewEntry?.();
              }
            }
          }}
          className="mantine-input"
          style={{ width: "100%", height: 32, minWidth: 0, ...deletedStyle }}
        />
        <input
          type="text"
          readOnly
          value={entry.isBackOrderItem ? "Yes" : "No"}
          className="mantine-input"
          style={{ width: "100%", height: 32, minWidth: 0, ...deletedStyle }}
        />
        <ActionIcon onClick={onRemove}>
          <IconX size={18} />
        </ActionIcon>
      </OrderEntryGridRow>
    );
  },
);
EntryRowComponent.displayName = "EntryRowComponent";
// Only re-render if returned or productCode changes
// const areEqual = (prev: OrderEntryRowProps, next: OrderEntryRowProps) =>
//   JSON.stringify(prev.entry) === JSON.stringify(next.entry);
const areEqual = (prev: OrderEntryRowProps, next: OrderEntryRowProps) =>
  prev.entry.productId === next.entry.productId &&
  prev.entry.ordered === next.entry.ordered &&
  prev.entry.supplied === next.entry.supplied &&
  prev.entry.ref === next.entry.ref &&
  prev.entry.unitPrice === next.entry.unitPrice &&
  prev.entry.eachPrice === next.entry.eachPrice &&
  prev.entry.cartonPrice === next.entry.cartonPrice &&
  prev.entry.isBackOrderItem === next.entry.isBackOrderItem;

export const OrderEntryRow = React.memo(EntryRowComponent, areEqual);
